/**
 * SaveDataPopover Component
 *
 * Popover for saving data_ids as personalized time series (qts_<name>).
 * Shows a dropdown to select which data_id to save and a text input for the name.
 * Uses a portal to render above all other content, positioned relative to anchor.
 */

import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { personalizedTSService } from "../../../services/PersonalizedTSService";
import { buildEnglishLabels } from "../../../features/data-labeling";
import styles from "./SaveDataPopover.module.css";
import logger from '../../../logging/logger';

interface SaveDataPopoverProps {
  /** Map of data_id -> description for available data to save */
  newDataIds: Record<string, string>;
  /** Callback when popover should close */
  onClose: () => void;
  /** Reference to the anchor element (save icon) */
  anchorRef: React.RefObject<HTMLElement>;
  /** Callback when save is successful */
  onSaveSuccess?: (qtsName: string, action: string) => void;
}

export const SaveDataPopover: React.FC<SaveDataPopoverProps> = ({
  newDataIds,
  onClose,
  anchorRef,
  onSaveSuccess,
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const { userUuid, getToken } = useAuth();
  
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [selectedDataId, setSelectedDataId] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [nameExists, setNameExists] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingName, setCheckingName] = useState(false);

  const dataIdEntries = useMemo(
    () =>
      buildEnglishLabels(
        Object.entries(newDataIds).map(([dataId, rawDescription]) => ({
          dataId,
          rawDescription,
        }))
      ),
    [newDataIds]
  );

  const normalizeNameSuffix = useCallback((rawName: string): string => {
    let suffix = rawName.trim();
    while (suffix.startsWith("qts_")) {
      suffix = suffix.slice(4);
    }
    return suffix;
  }, []);

  const nameSuffix = useMemo(() => normalizeNameSuffix(name), [name, normalizeNameSuffix]);
  const fullName = useMemo(() => (nameSuffix ? `qts_${nameSuffix}` : ""), [nameSuffix]);
  const nameValidationError = useMemo(() => {
    if (!nameSuffix) return null;
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(nameSuffix)) {
      return "Use letters, numbers, and underscores only (must start with a letter or underscore)";
    }
    if (fullName.length > 64) {
      return "Name is too long (maximum 64 characters including qts_)";
    }
    return null;
  }, [nameSuffix, fullName]);

  // Auto-select first data_id if only one
  useEffect(() => {
    if (dataIdEntries.length === 1) {
      setSelectedDataId(dataIdEntries[0].dataId);
    }
  }, [dataIdEntries]);

  // Calculate position based on anchor element
  useEffect(() => {
    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    };

    updatePosition();
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Check if name exists (debounced)
  useEffect(() => {
    if (!fullName || !userUuid || !!nameValidationError) {
      setNameExists(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingName(true);
      try {
        const token = await getToken();
        if (!token) return;
        const result = await personalizedTSService.checkNameExists(userUuid, token, fullName);
        setNameExists(result.exists);
      } catch (err) {
        logger.error("Failed to check name:", err);
      } finally {
        setCheckingName(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [fullName, userUuid, getToken, nameValidationError]);

  const handleSave = useCallback(async () => {
    if (!selectedDataId || !fullName || !userUuid || nameValidationError) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        setError("Authentication required");
        return;
      }

      const result = await personalizedTSService.saveMapping(userUuid, token, {
        name: fullName,
        data_id: selectedDataId,
        description: newDataIds[selectedDataId],
      });

      onSaveSuccess?.(result.qts_name, result.action);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsLoading(false);
    }
  }, [selectedDataId, fullName, userUuid, getToken, newDataIds, onSaveSuccess, onClose, nameValidationError]);

  const isValid = selectedDataId && !!fullName && !nameValidationError;
  const buttonText = nameExists ? "Overwrite" : "Save";
  const buttonClass = nameExists ? styles.overwriteButton : styles.saveButton;

  return createPortal(
    <div
      ref={popoverRef}
      className={styles.popover}
      style={{ top: position.top, left: position.left }}
    >
      <div className={styles.header}>
        <span className={styles.title}>Save Data</span>
      </div>
      <div className={styles.content}>
        {/* Data selection dropdown (only if multiple data_ids) */}
        {dataIdEntries.length > 1 && (
          <div className={styles.paramRow}>
            <label className={styles.label}>Select Data</label>
            <select
              className={styles.select}
              value={selectedDataId}
              onChange={(e) => setSelectedDataId(e.target.value)}
            >
              <option value="">Select data to save...</option>
              {dataIdEntries.map(({ dataId, label }) => (
                <option key={dataId} value={dataId}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Single data display (if only one data_id) */}
        {dataIdEntries.length === 1 && (
          <div className={styles.paramRow}>
            <label className={styles.label}>Data</label>
            <div className={styles.singleDataDisplay}>
              {dataIdEntries[0].label}
            </div>
          </div>
        )}

        {/* Name input */}
        <div className={styles.paramRow}>
          <label className={styles.label}>
            Name
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.inputWrapper}>
            <span className={styles.prefix}>qts_</span>
            <input
              type="text"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my_data"
              autoFocus
            />
          </div>
          {nameValidationError && (
            <span className={styles.warning}>{nameValidationError}</span>
          )}
          {checkingName && (
            <span className={styles.hint}>Checking...</span>
          )}
          {nameExists && !checkingName && (
            <span className={styles.warning}>Name already exists</span>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className={styles.error}>{error}</div>
        )}

        {/* Action button */}
        <button
          className={`${styles.actionButton} ${buttonClass}`}
          onClick={handleSave}
          disabled={!isValid || isLoading}
        >
          {isLoading ? "Saving..." : buttonText}
        </button>
      </div>
    </div>,
    document.body
  );
};
